import React from "react";
import i18n from "../../../../i18n/i18n";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../../common/models/httpError";
import {RouteComponentProps, withRouter} from "react-router";
import {Api} from "../../../../helpers/api";
import {HttpError} from "../../../../common/components/shared/httpError/httpError";
import Loader from "../../../../common/components/shared/loader/loader";
import {RouteParamsModel} from "../../../../common/models/routeParams";
import SimpleInfoElement from "../../../../common/components/shared/simpleInfoElement/simpleInfoElement";
import Table from "../../../../common/components/shared/table/table";
import TransportationActions from "../../transportationActions/transportationActions";
import {TicketDetailModel} from "../../../models/ticket";
import { Utils } from "../../../../helpers/utils";
import ReloadButton from "../../../../common/components/shared/reloadButton/reloadButton";
import {TicketService} from "../../../services/ticketService";
import { ValidatedValue } from "../../../../helpers/validatedValue";
import Validator from "../../../../helpers/validator";
import { errorMessages } from "../../../../helpers/errorMessage";
import Button from "../../../../common/components/shared/button/button";
import {ReactComponent as AllowIcon} from "../../../../statics/icons/icon_allow.svg";
import {ReactComponent as NotAllowIcon} from "../../../../statics/icons/icon_discard.svg";
import {ReactComponent as EditIcon} from "../../../../statics/icons/icon_edit.svg";
import AllowOrNotAllowPopup from "../../../../common/components/shared/allowOrNotAllowPopup/allowOrNotAllowPopup";
import {IUserStore} from "../../../../administration/stores/userStore";
import {storesContext} from "../../../../common/stores/stores";
import {observer} from "mobx-react";
import {NavLink} from "react-router-dom";
import {routingAddresses} from "../../../../helpers/routing";

interface TicketDetailsState {
    ticketData?: TicketDetailModel | null;
    response: AxiosResponse<HttpErrorModel[] | any> | undefined;
    id: string | null;
    displayPopup: "allowPopup" | "notAllowPopup" | null;
    allowResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    notAllowResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    selectedLocation: ValidatedValue<string>;
    isPopupActionWaiting: boolean;
}

class TicketDetails extends React.Component <RouteComponentProps, TicketDetailsState> {
    static contextType = storesContext;

    constructor(props: any) {
        super(props);

        const notEmptyValidator = {
            name: "valueIsNotEmpty",
            fn: Validator.isNotEmpty,
            errorMessage: errorMessages.required
        };

        this.state = {
            ticketData: null,
            response: undefined,
            id: null,
            displayPopup: null,
            allowResponse: undefined,
            notAllowResponse: undefined,
            selectedLocation: new ValidatedValue("", [notEmptyValidator]),
            isPopupActionWaiting: false
        };
    }

    async componentDidMount() {
        const params = this.props.match.params as RouteParamsModel;
        const response = await TicketService.getTicket(params.id);

        this.setState({
            ticketData: Api.isSuccess(response) ? response?.data : null,
            response,
            id: params.id
        });
    }

    public render(): JSX.Element {
        return (
            <div className="transportation-ticket-details">
                <div className="content transportation-ticket-content">
                    {this.renderContent()}
                    {this.state.displayPopup && this.renderAllowOrNotAllowPopups()}
                </div>
            </div>
        );
    }

    private renderContent() {
        if (!Api.isSuccess(this.state.response)) {
            return <HttpError
                response={this.state.response}
                cause={i18n.t("transportation.ticket.errors.detailsRetrievalErrorSuffix")}
            />;
        } else if (this.state.ticketData) {
            return this.renderDetails();
        } else {
            return (
                <Loader/>
            );
        }
    }

    private renderDetails() {
        const userStore = this.context.userStore as IUserStore;

        return (
            <>
                <TransportationActions
                    orderId={this.state.ticketData?.id || ""}
                    element="ticket"
                />
                <div>
                    <div className="header" data-testid="header">
                        {i18n.t("transportation.ticket.preview.header", {number: this.state.ticketData!.id})}
                        <div className="actions">
                            {userStore.displayKioskPassageDirection && (
                                <>
                                    <Button
                                        className="not-allow"
                                        type="primary"
                                        text={i18n.t("transportation.order.actions.notAllow")}
                                        endIcon={<NotAllowIcon/>}
                                        onClick={() => {
                                            this.state.selectedLocation.clearValidation();
                                            this.onAllowOrNorAllowAction("notAllowPopup");
                                        }}
                                        testId="not-allow-btn"
                                    />
                                    <Button
                                        className="allow"
                                        type="primary"
                                        text={i18n.t("transportation.order.actions.allow")}
                                        endIcon={<AllowIcon/>}
                                        onClick={() => {
                                            this.state.selectedLocation.clearValidation();
                                            this.onAllowOrNorAllowAction("allowPopup");
                                        }}
                                        testId="allow-btn"
                                    />
                                </>
                            )}
                            {userStore.canModifyTicket && (
                                <NavLink
                                    to={`${routingAddresses.ticket.modify}/${this.state.id}`}
                                    data-testid="modify-ticket"
                                    className="action-button"
                                >
                                    <Button
                                        type="primary"
                                        text={i18n.t("transportation.ticket.modifyTicket.button")}
                                        endIcon={<EditIcon/>}
                                    />
                                </NavLink>
                            )}
                            <ReloadButton
                                reloadFunc={this.reload.bind(this)}
                                testId="ticket-details-reload-btn"
                            />
                        </div>
                    </div>

                    {this.renderGeneralData()}
                    {this.renderTransportation()}
                    {this.renderVoyage()}
                    {this.renderCargo()}
                    {this.renderDangerousGoods()}
                </div>
            </>
        );
    }

    private renderGeneralData() {
        return (
            <div className="section">
                <div className="section-header">
                    {i18n.t("transportation.ticket.details.general.header")}
                </div>
                <div className="section-content">
                    <SimpleInfoElement
                        label={i18n.t("transportation.ticket.details.general.properties.status")}
                        value={`${i18n.t(`orderStatus.${this.state.ticketData!.status}`)}`}
                        testId="status"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.ticket.details.general.properties.cargoType")}
                        value={`${i18n.t(`cargoType.${this.state.ticketData!.cargoType}`)}`}
                        testId="cargo-type"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.ticket.details.general.properties.barcodeNo")}
                        value={this.state.ticketData!.barcodeNo}
                        testId="barcode-no"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.ticket.details.general.properties.currentRoute")}
                        value={this.state.ticketData!.currentRoute}
                        testId="current-route"

                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.ticket.details.general.properties.gateNo")}
                        value={this.state.ticketData!.gateNo}
                        testId="gate-no"

                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.ticket.details.general.properties.createdAt")}
                        value={Utils.renderTime(this.state.ticketData!.createdAt)}
                        testId="created-at"
                    />
                </div>
            </div>
        );
    }

    private renderTransportation() {
        return (
            <div className="section">
                <div className="section-header">
                    {i18n.t("transportation.ticket.details.transportation.header")}
                </div>
                <div className="section-content">
                    <SimpleInfoElement
                        label={i18n.t("transportation.ticket.details.transportation.properties.trailerRegNo")}
                        value={this.state.ticketData!.trailerRegNo}
                        testId="trailer-reg-no"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.ticket.details.transportation.properties.transportCompany")}
                        value={this.state.ticketData!.transportCompany}
                        testId="transport-company"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.ticket.details.transportation.properties.lorryRegNo")}
                        value={this.state.ticketData!.lorryRegNo}
                        testId="lorry-reg-no"
                    />
                </div>
            </div>
        );
    }

    private renderVoyage() {
        return (
            <div className="section">
                <div className="section-header">
                    {i18n.t("transportation.ticket.details.voyage.header")}
                </div>
                <div className="section-content">
                    <SimpleInfoElement
                        label={i18n.t("transportation.ticket.details.voyage.properties.shipownerName")}
                        value={this.state.ticketData!.shipownerName}
                        testId="shipowner-name"

                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.ticket.details.voyage.properties.voyageNo")}
                        value={this.state.ticketData!.voyageNo}
                        testId="voyage-no"

                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.ticket.details.voyage.properties.arrivalDepartureTime")}
                        value={Utils.renderTime(this.state.ticketData!.arrivalDepartureTime)}
                        testId="arrival-departure-time"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.ticket.details.voyage.properties.ferryName")}
                        value={this.state.ticketData!.ferryName}
                        testId="ferry-name"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.ticket.details.voyage.properties.originDestinationPort")}
                        value={this.state.ticketData!.originDestinationPort}
                        testId="origin-destination-port"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.ticket.details.voyage.properties.originDestinationCountry")}
                        value={this.state.ticketData!.originDestinationCountry}
                        testId="origin-destination-country"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.ticket.details.voyage.properties.bookingNo")}
                        value={this.state.ticketData!.bookingNo}
                        testId="booking-no"
                    />
                </div>
            </div>
        );
    }

    private renderCargo() {
        return (
            <div className="section">
                <div className="section-header">
                    {i18n.t("transportation.ticket.details.cargo.header")}
                </div>
                <div className="section-content">
                    <SimpleInfoElement
                        label={i18n.t("transportation.ticket.details.cargo.properties.cargoDesc")}
                        value={this.state.ticketData!.cargoDesc}
                        testId="cargo-desc"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.ticket.details.cargo.properties.weight")}
                        value={this.state.ticketData!.weight}
                        unit="kg"
                        testId="weight"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.ticket.details.cargo.properties.height")}
                        value={this.state.ticketData!.height}
                        unit="m"
                        testId="height"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.ticket.details.cargo.properties.width")}
                        value={this.state.ticketData!.width}
                        unit="m"
                        testId="width"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.ticket.details.cargo.properties.length")}
                        value={this.state.ticketData!.length}
                        unit="m"
                        testId="length"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.ticket.details.cargo.properties.securingDeclaration")}
                        value={Utils.renderBooleanYesNoValue(this.state.ticketData!.securingDeclaration, true)}
                        testId="securing-declaration"
                    />
                </div>
            </div>
        );
    }

    private renderDangerousGoods() {
        const properties = ["unCode", "packing", "packingQuantity", "packingGroup", "weight"];
        const headers = properties.map(key => i18n.t("transportation.ticket.details.dangerousGoods.properties." + key));

        return (
            <div className="section">
                <div className="section-header">
                    {i18n.t("transportation.ticket.details.dangerousGoods.header")}
                </div>
                <Table
                    headers={headers}
                    rows={this.state.ticketData!.dangerousGoods || []}
                    properties={properties}
                    renderCell={this.renderDangerousGoodsListCell.bind(this)}
                    testId="dangerous-goods"
                    numberOfElements={this.numberOfElementsForDangerousGood()}
                    currentPage={0}
                />
            </div>
        );
    }

    private renderDangerousGoodsListCell(row: any, property: string): JSX.Element {
        if (property === "unCode" && row.unCode && row.unCode.id && row.unCode.name) {
            return <>
                <span className="un-code-id">{row.unCode.id || ""}: </span>
                <span>{row.unCode.name || ""}</span>
            </>;
        }

        return row[property];
    }

    private renderAllowOrNotAllowPopups() {
        const isAllowPopup = this.state.displayPopup === "allowPopup";

        return (
            <AllowOrNotAllowPopup
                popupType={this.state.displayPopup}
                orderType="ticket"
                closePopup={this.closePopup.bind(this)}
                confirm={this.confirmAllowOrNotAllow.bind(this, isAllowPopup)}
                response={isAllowPopup ? this.state.allowResponse : this.state.notAllowResponse}
                isOrderPopup
                orderId={this.state.ticketData ? this.state.ticketData!.id : undefined}
                onChangeDropdown={(newValue: any) => {
                    this.state.selectedLocation.setValue(newValue);
                }}
                selectedLocation={this.state.selectedLocation}
                isDownloadLocations
                isWaiting={this.state.isPopupActionWaiting}
            />
        );
    }

    private async reload() {
        this.setState({ticketData: undefined});
        const response = this.state.id ? await TicketService.getTicket(this.state.id!) : undefined;

        this.setState({
            ticketData: Api.isSuccess(response) ? response?.data : null,
            response
        });
    }

    private numberOfElementsForDangerousGood() {
        if (this.state.ticketData!.dangerousGoods && this.state.ticketData!.dangerousGoods.length > 0) {
            return this.state.ticketData!.dangerousGoods.length;
        } else return 0;
    }

    private onAllowOrNorAllowAction(displayPopup: "allowPopup" | "notAllowPopup" | null) {
        this.state.selectedLocation.setValue("");
        this.setState({displayPopup});
    }

    private closePopup() {
        this.setState({
            displayPopup: null,
            allowResponse: undefined,
            notAllowResponse: undefined
        });
    }

    public get isValid() {
        this.state.selectedLocation.validate();

        return this.state.selectedLocation.isValid;
    }

    private async confirmAllowOrNotAllow(isAllow: boolean) {
        this.setState({allowResponse: undefined, notAllowResponse: undefined, response: undefined, isPopupActionWaiting: true});

        if (this.isValid) {
            const response = await TicketService.allowOrNotAllowLocation(this.state.ticketData!.id, this.state.selectedLocation.value, isAllow);

            if (isAllow) {
                this.setState({allowResponse: response});
            } else {
                this.setState({notAllowResponse: response});
            }

            if (Api.isSuccess(response)) {
                const response = await TicketService.getTicket(this.state.ticketData!.id);

                this.setState({
                    ticketData: response?.data || null,
                    response
                });

                this.closePopup();
            }
        }

        Utils.scrollToFirstErrorMessage();
        this.setState({isPopupActionWaiting: false});
    }
}

export default withRouter(observer(TicketDetails));
